import type { WorkbookInstance } from "@fortune-sheet/react"
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { ExcelEditorHandle } from "@/components/ExcelEditor"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { PowerBar } from "@/features/material-compliance/components/declarations/review/studio/PowerBar"
import {
  textTerms,
  type FilterToken,
} from "@/features/material-compliance/components/declarations/review/studio/TableSearch"
import {
  orderWorkbookSources,
  proofDecorations,
  type WorkbookSource,
} from "@/features/material-compliance/components/declarations/review/studio/workbookProofs"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"

import { LazyExcelEditorPreview } from "./ExcelFileViewer"
import { ExcelJSViewer, type WorkbookJump } from "./ExcelJSViewer"
import { ExcelEditorBoundary } from "./ExcelPreviewState"
import type { RangeDecoration } from "./rangeDecorations"
import {
  hitAt,
  hitPosition,
  SEARCH_RANGE_CAP,
  searchDecorations,
  searchSheetTerms,
  sheetsWithHits,
  stepHit,
  totalHits,
  type HitCursor,
  type SearchableSheet,
  type SheetSearch,
} from "./workbookSearch"

/**
 * THE DECLARATION FLOW'S WORKBOOK PANEL — the cell-modality twin of
 * `DeclarationDocumentViewer`, and the "one adapter per flow" of Concept "Excel
 * declarations and cell evidence", Part VII.
 *
 * The same gestures over a different surface: ⌘F with every match marked yellow
 * and stepping across sheets, the selected statement's proofs tinted in its own
 * palette colour, and the ◀▶ source track walking them in document order —
 * `(surface ordinal, row, col)` here, `(page, y, x)` there. The bar is the shared
 * PowerBar, so the dock, the chips and the ⓘ→✕ toggle are identical.
 *
 * Two rules it exists to keep:
 *
 *  - **The active proof is the native selection.** Clicking or stepping to a
 *    proof activates its sheet, selects its range and scrolls to it. The
 *    selection ring is freeze-correct and reads as "you are here"; the decoration
 *    layer is the persistent tint. Selection is the cursor.
 *  - **Search never selects.** A hit only scrolls, and is marked instead — so
 *    find-in-document cannot fight the reviewer's own cursor.
 *
 * `WorkbookInstance` stops here: nothing above this component ever handles a
 * renderer type. Decorations, jumps and sources are all workbook coordinates.
 */

/** Re-evaluating a 200k-cell matrix costs ~150 ms, so keystrokes are pooled. */
const SEARCH_DEBOUNCE_MS = 200

/**
 * No proofs, as ONE stable array: a fresh `[]` default per render would rebuild
 * the decoration layer on every render, and each layer is a full-canvas redraw.
 */
const NO_SOURCES: WorkbookSource[] = []

/**
 * Excel's last column (XFD), 0-based: a hinted row's tint runs to the end of
 * the row. Both renderers clamp a decoration to the sheet's own width.
 */
const LAST_COLUMN = 16_383
/**
 * Fuchsia: stands out on the white, grey and yellow fills inquiry templates
 * use, and is none of the eight statement tints nor the yellow search mark.
 */
const ROW_FOCUS_FILL = "rgba(217, 70, 239, 0.18)"
const ROW_FOCUS_BORDER = "rgb(192, 38, 211)"

interface DeclarationWorkbookViewerProps {
  /** The workbook's bytes as a URL. Undefined ⇒ nothing to show yet. */
  fileUrl: string | undefined
  /**
   * The step's cell proofs, already resolved by `buildWorkbookSources` — the
   * screen owns that call, because only it knows the statement list order that
   * fixes each statement's colour.
   */
  sources?: WorkbookSource[]
  /** The screen's selected statement: the one whose proofs are tinted. */
  activeStatementId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Forwarded to the bar: a bump focuses the find input even while open. */
  findFocusNonce?: number
  tokens: FilterToken[]
  search: string
  onTokensChange: (tokens: FilterToken[]) => void
  onSearchChange: (s: string) => void
  placeholder?: string
  /** The source track's cursor, reported whenever it moves. */
  onCurrentSource?: (key: string | null) => void
  /**
   * Land the track on one source BY KEY — the card side's citation click, which
   * is the workbook twin of handing the PDF viewer a `highlight`: a cell proof
   * IS an address, so the gesture names the mark rather than text to hunt for.
   * A FRESH OBJECT is a fresh request (same contract as `WorkbookJump`), so
   * clicking the same line twice lands twice.
   */
  focusRequest?: { key: string } | null
  /**
   * Land on one sheet row and tint it — the card side's reader hint click
   * ("Auszufüllen · Zeile 766"). `row` is 1-based, as the hint names it. A hint
   * is no proof, so this never moves the source track. A FRESH OBJECT is a
   * fresh request; null drops the tint.
   */
  rowFocus?: { sheet: string; row: number } | null
  /**
   * The bar's own ◀▶ (a proof or a search hit) moved the view away from the
   * hinted row. The owner of `rowFocus` drops it, so the row's tint does not
   * linger over wherever the reviewer went next. Only called while a row is
   * hinted.
   */
  onRowFocusEnd?: () => void
  className?: string
}

export const DeclarationWorkbookViewer: React.FC<
  DeclarationWorkbookViewerProps
> = ({
  fileUrl,
  sources = NO_SOURCES,
  activeStatementId = null,
  open,
  onOpenChange,
  findFocusNonce,
  tokens,
  search,
  onTokensChange,
  onSearchChange,
  placeholder,
  onCurrentSource,
  focusRequest = null,
  rowFocus = null,
  onRowFocusEnd,
  className = "",
}) => {
  const { translate } = useTranslation()
  // EXCEL_EDITOR_V2: the new editor in place of Fortune Sheet, same bar on top.
  const newEditor = useFeatureFlag("EXCEL_EDITOR_V2")
  const workbookRef = useRef<WorkbookInstance | null>(null)
  const editorRef = useRef<ExcelEditorHandle>(null)
  // Bumped by every load, because the workbook is remounted then: the sheet
  // matrices search reads are new objects, and any result over the old ones is
  // stale.
  const [generation, setGeneration] = useState(0)
  const [jump, setJump] = useState<WorkbookJump | null>(null)

  const onLoad = useCallback(() => setGeneration((g) => g + 1), [])
  // Stable, so the memoized viewer is not re-rendered by every keystroke above it.
  const toggleOpen = useCallback(
    () => onOpenChange(!open),
    [onOpenChange, open],
  )

  // ── the source track ──────────────────────────────────────────────────────
  // Only the selected statement's proofs are tinted and walked, exactly as the
  // PDF viewer paints one statement at a time.
  const track = useMemo(
    () =>
      orderWorkbookSources(
        activeStatementId === null
          ? sources
          : sources.filter((s) => s.statementId === activeStatementId),
      ),
    [sources, activeStatementId],
  )
  const [curKey, setCurKey] = useState<string | null>(null)
  const navPos = track.findIndex((s) => s.key === curKey)
  const curSource = navPos >= 0 ? track[navPos] : null

  useEffect(() => {
    onCurrentSource?.(curSource?.key ?? null)
  }, [curSource, onCurrentSource])

  // The cursor follows the track: when the selection changes under it, it moves
  // to that statement's first proof WITHOUT scrolling — only a real gesture
  // (◀▶, a click) moves the document.
  useEffect(() => {
    if (track.length === 0) {
      setCurKey(null)
      return
    }
    setCurKey((key) =>
      key !== null && track.some((s) => s.key === key) ? key : track[0].key,
    )
  }, [track])

  /** Land on a proof: the native selection plus a scroll. */
  const focusSource = useCallback((source: WorkbookSource) => {
    setCurKey(source.key)
    if (!source.sheetKnown || source.range === null) return
    setJump({ sheet: source.sheet, range: source.range })
  }, [])

  // Selection sync: when the screen selects a different statement, land on its
  // first landable proof so its evidence comes into view — the same round trip
  // the PDF viewer makes on a card selection. Keyed on the statement alone: a
  // ◀▶ walk inside it must not re-jump to its first proof.
  useEffect(() => {
    if (activeStatementId == null) return
    const first = track.find((s) => s.sheetKnown && s.range !== null)
    if (first) focusSource(first)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatementId])

  // The card side asked for one mark by key. Resolved against the UNFILTERED
  // source list: the clicked line may belong to a statement that is not the
  // selected one, and the jump must still land (the screen selects the card
  // through its own gesture, not through this one).
  useEffect(() => {
    if (!focusRequest) return
    const source = sources.find((s) => s.key === focusRequest.key)
    if (source) focusSource(source)
    // A request navigates once, when it is made — never re-fired by a source
    // list refresh underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest])

  // A reader hint names a row, not a proof. The jump goes to the row's FIRST
  // cell and never selects: a whole-row target would centre the view on the
  // middle of the row, far to the right. The row tint below marks the row.
  useEffect(() => {
    if (!rowFocus) return
    const row = rowFocus.row - 1
    setJump({
      sheet: rowFocus.sheet,
      range: { startRow: row, endRow: row, startColumn: 0, endColumn: 0 },
      select: false,
    })
  }, [rowFocus])

  const stepSource = useCallback(
    (dir: 1 | -1) => {
      const landable = track.filter((s) => s.sheetKnown && s.range !== null)
      if (landable.length === 0) return
      const at = landable.findIndex((s) => s.key === curKey)
      const next =
        at < 0
          ? dir === 1
            ? 0
            : landable.length - 1
          : (at + dir + landable.length) % landable.length
      focusSource(landable[next])
      if (rowFocus) onRowFocusEnd?.()
    },
    [track, curKey, focusSource, rowFocus, onRowFocusEnd],
  )

  // ── search ────────────────────────────────────────────────────────────────
  const terms = useMemo(() => textTerms(tokens, search), [tokens, search])
  const termKey = terms.join("\0")
  const [debouncedKey, setDebouncedKey] = useState("")
  useEffect(() => {
    if (!open) {
      setDebouncedKey("")
      return
    }
    const timer = window.setTimeout(
      () => setDebouncedKey(termKey),
      SEARCH_DEBOUNCE_MS,
    )
    return () => window.clearTimeout(timer)
  }, [termKey, open])

  const [results, setResults] = useState<SheetSearch[]>([])
  const [cursor, setCursor] = useState<HitCursor | null>(null)

  useEffect(() => {
    const wanted = debouncedKey.split("\0").filter(Boolean)
    if (newEditor) {
      const terms = wanted.map((term) => term.trim()).filter(Boolean)
      const editor = editorRef.current
      if (terms.length === 0 || !editor) {
        setResults([])
        setCursor(null)
        return
      }
      // The editor searches its own cells in one pass: the shown text,
      // case-insensitive, every sheet with a tab (hidden ones are revealed).
      setResults(
        editor.search(terms).map((result) => ({
          ordinal: result.sheet.index + 1,
          sheet: result.sheet.name,
          hits: result.hits,
          ranges: result.ranges,
          shown: Math.min(result.ranges.length, SEARCH_RANGE_CAP),
        })),
      )
      setCursor(null)
      return
    }
    const workbook = workbookRef.current
    if (wanted.length === 0 || !workbook) {
      setResults([])
      setCursor(null)
      return
    }

    // Read the matrices straight off the renderer — no copy, and `data` is the
    // dense matrix its own search helper expects.
    const sheets: SearchableSheet[] = workbook
      .getAllSheets()
      .map((sheet, index) => ({
        ordinal: index + 1,
        name: sheet.name,
        matrix: sheet.data ?? [],
      }))
    const active = (() => {
      try {
        return workbook.getSheet().name
      } catch {
        return sheets[0]?.name
      }
    })()

    // THE SHEET THE REVIEWER IS LOOKING AT IS ANSWERED FIRST; the rest are still
    // searched one per tick, so a wide workbook cannot freeze a keystroke, but
    // they land TOGETHER. Every commit hands the renderer a new decoration layer,
    // which redraws the whole canvas, while only the visible sheet's marks are
    // ever painted — so landing them one by one bought a redraw per sheet for
    // nothing. The count in the bar is the visible sheet's, then the total,
    // which is honest: a running total, never a wrong one.
    const order = [...sheets].sort(
      (a, b) =>
        Number(b.name === active) - Number(a.name === active) ||
        a.ordinal - b.ordinal,
    )
    const empty = sheets.map((sheet) => ({
      ordinal: sheet.ordinal,
      sheet: sheet.name,
      hits: [],
      ranges: [],
      shown: 0,
    }))
    setResults(empty)
    setCursor(null)

    let cancelled = false
    let timer = 0
    const land = (done: SheetSearch[]) =>
      setResults((previous) =>
        previous.map(
          (entry) =>
            done.find((result) => result.ordinal === entry.ordinal) ?? entry,
        ),
      )
    const rest: SheetSearch[] = []
    const evaluate = (i: number) => {
      if (cancelled) return
      if (i >= order.length) {
        if (rest.length > 0) land(rest)
        return
      }
      const done = searchSheetTerms(order[i], wanted)
      if (i === 0) land([done])
      else rest.push(done)
      timer = window.setTimeout(() => evaluate(i + 1), 0)
    }
    evaluate(0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [debouncedKey, generation, newEditor])

  const hits = totalHits(results)
  const activeHit = hitAt(results, cursor)

  const stepMatch = useCallback(
    (dir: 1 | -1) => {
      const next = stepHit(results, cursor, dir)
      setCursor(next)
      const at = hitAt(results, next)
      if (!at) return
      // Stepping activates the sheet and scrolls — but never selects.
      setJump({
        sheet: at.sheet,
        range: {
          startRow: at.hit.row,
          endRow: at.hit.row,
          startColumn: at.hit.column,
          endColumn: at.hit.column,
        },
        select: false,
      })
      if (rowFocus) onRowFocusEnd?.()
    },
    [results, cursor, rowFocus, onRowFocusEnd],
  )

  // ── the one layer ─────────────────────────────────────────────────────────
  const decorations = useMemo<RangeDecoration[]>(() => {
    const proofs = proofDecorations(track, curKey, {
      hidden: translate("MC_DECL_WB_HIDDEN"),
      readBy: (readBy) =>
        readBy === "code"
          ? translate("MC_DECL_WB_READ_BY_CODE")
          : readBy === "model"
            ? translate("MC_DECL_WB_READ_BY_MODEL")
            : null,
    })
    // The hinted row rides over the proof tints but under the search marks, so
    // a hit inside that row stays visible.
    const row: RangeDecoration[] = rowFocus
      ? [
          {
            id: `wb-row-focus:${rowFocus.sheet}:${rowFocus.row}`,
            sheet: rowFocus.sheet,
            range: {
              startRow: rowFocus.row - 1,
              endRow: rowFocus.row - 1,
              startColumn: 0,
              endColumn: LAST_COLUMN,
            },
            fill: ROW_FOCUS_FILL,
            border: ROW_FOCUS_BORDER,
            className: "wb-row-focus",
          },
        ]
      : []
    // Search marks ride over the proof tints, and every sheet's marks are handed
    // over at once — the renderer paints only the active sheet's.
    const marks = results.flatMap((result) =>
      searchDecorations(
        result,
        activeHit && activeHit.sheet === result.sheet ? activeHit.hit : null,
      ),
    )
    return [...proofs, ...row, ...marks]
  }, [track, curKey, rowFocus, results, activeHit, translate])

  const cappedIn = results.filter((r) => r.ranges.length > r.shown)
  const capped = cappedIn.reduce(
    (totals, r) => ({
      shown: totals.shown + r.shown,
      total: totals.total + r.ranges.length,
    }),
    { shown: 0, total: 0 },
  )

  const provenance = curSource
    ? [
        curSource.hidden ? translate("MC_DECL_WB_HIDDEN") : null,
        curSource.readBy === "code"
          ? translate("MC_DECL_WB_READ_BY_CODE")
          : curSource.readBy === "model"
            ? translate("MC_DECL_WB_READ_BY_MODEL")
            : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : ""

  return (
    <div className={cn("doc-viewer", className)}>
      <div className="min-h-0 flex-1">
        {fileUrl && newEditor ? (
          <ExcelEditorBoundary>
            <LazyExcelEditorPreview
              ref={editorRef}
              source={fileUrl}
              decorations={decorations}
              jump={jump}
              onFind={toggleOpen}
              onLoad={onLoad}
              // As below: proofs in what the supplier hid must stay landable.
              revealHidden
            />
          </ExcelEditorBoundary>
        ) : fileUrl ? (
          <ExcelJSViewer
            file={fileUrl}
            workbookRef={workbookRef}
            decorations={decorations}
            jump={jump}
            onFind={toggleOpen}
            onLoad={onLoad}
            // The pipeline read what the supplier hid, so its proofs have to be
            // landable. Un-hiding is view-model only — the file is untouched —
            // and every such proof still carries its "hidden" badge.
            revealHidden
          />
        ) : null}
      </div>

      <PowerBar
        open={open}
        onToggle={toggleOpen}
        focusNonce={findFocusNonce}
        tokens={tokens}
        search={search}
        onTokensChange={onTokensChange}
        onSearchChange={onSearchChange}
        placeholder={placeholder}
        extra={
          <>
            {terms.length > 0 && (
              <span
                className="doc-bar-count doc-bar-count-fixed"
                data-testid="wb-hit-count"
                title={translate("MC_DECL_WB_HITS_IN_SHEETS", {
                  sheets: sheetsWithHits(results),
                })}
              >
                {hitPosition(results, cursor)}/{hits}
              </span>
            )}
            <button
              type="button"
              className="doc-bar-btn"
              onClick={() => stepMatch(-1)}
              disabled={hits === 0}
              aria-label={translate("MC_DECL_DOC_PREV_MATCH")}
            >
              <ChevronUp size={13} />
            </button>
            <button
              type="button"
              className="doc-bar-btn"
              onClick={() => stepMatch(1)}
              disabled={hits === 0}
              aria-label={translate("MC_DECL_DOC_NEXT_MATCH")}
            >
              <ChevronDown size={13} />
            </button>
            {cappedIn.length > 0 && (
              // A capped result is a stated gap, never a silent truncation.
              <span className="doc-bar-count" data-testid="wb-search-capped">
                {translate("MC_DECL_WB_SEARCH_CAPPED", capped)}
              </span>
            )}
            {track.length > 0 && (
              <>
                <span className="doc-bar-divider" />
                <button
                  type="button"
                  className="doc-bar-btn"
                  onClick={() => stepSource(-1)}
                  aria-label={translate("MC_DECL_DOC_PREV_QUOTE")}
                >
                  <ChevronLeft size={14} />
                </button>
                <span
                  className="doc-bar-count doc-bar-count-fixed"
                  data-testid="wb-source-count"
                >
                  {Math.max(navPos, 0) + 1}/{track.length}
                </span>
                <button
                  type="button"
                  className="doc-bar-btn"
                  onClick={() => stepSource(1)}
                  aria-label={translate("MC_DECL_DOC_NEXT_QUOTE")}
                >
                  <ChevronRight size={14} />
                </button>
                <span className="doc-bar-count" data-testid="wb-source-where">
                  {curSource === null
                    ? ""
                    : curSource.sheetKnown
                      ? // Ordinal → sheet NAME, never "page 3".
                        [
                          translate("MC_DECL_WB_AT", {
                            sheet: curSource.sheet,
                            ref: curSource.ref,
                          }),
                          provenance,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : translate("MC_DECL_WB_SHEET_UNKNOWN")}
                </span>
              </>
            )}
          </>
        }
      />
    </div>
  )
}
